import { createApi } from '@reduxjs/toolkit/query/react';
import { customHandoverBaseQuery } from '../customBaseQuery';
import {
  DashboardFilter,
  DashboardListResponse,
  HandoverDetailResponse,
  NoteEntry,
} from '../../features/managerDashboard/types';

export const managerDashboardApi = createApi({
  reducerPath: 'managerDashboardApi',
  baseQuery: customHandoverBaseQuery,
  tagTypes: ['Dashboard', 'DashboardDetail'],
  refetchOnMountOrArgChange: 30,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getDashboard: builder.query<DashboardListResponse, DashboardFilter>({
      query: filter => ({ url: `/dashboard?filter=${filter}`, method: 'get' }),
      providesTags: ['Dashboard'],
    }),

    getDashboardDetail: builder.query<HandoverDetailResponse, string>({
      query: departureId => ({ url: `/dashboard/${departureId}`, method: 'get' }),
      providesTags: (_, __, id) => [{ type: 'DashboardDetail', id }],
    }),

    assignIncomingEmployee: builder.mutation<
      { message: string },
      { departureId: string; incomingEmployeeEmail: string }
    >({
      query: ({ departureId, incomingEmployeeEmail }) => ({
        url: `/dashboard/${departureId}/assign`,
        method: 'put',
        body: { incomingEmployeeEmail },
      }),
      invalidatesTags: (_, __, { departureId }) => [
        'Dashboard',
        { type: 'DashboardDetail', id: departureId },
      ],
    }),

    addNote: builder.mutation<
      { message: string; data: NoteEntry },
      { departureId: string; content: string }
    >({
      query: ({ departureId, content }) => ({
        url: `/dashboard/${departureId}/note`,
        method: 'post',
        body: { content },
      }),
      invalidatesTags: (_, __, { departureId }) => ['Dashboard', { type: 'DashboardDetail', id: departureId }],
    }),

    updateNote: builder.mutation<
      { message: string },
      { departureId: string; noteId: string; content: string }
    >({
      query: ({ departureId, noteId, content }) => ({
        url: `/dashboard/${departureId}/note/${noteId}`,
        method: 'put',
        body: { content },
      }),
      invalidatesTags: (_, __, { departureId }) => [{ type: 'DashboardDetail', id: departureId }],
    }),

    deleteNote: builder.mutation<
      { message: string },
      { departureId: string; noteId: string }
    >({
      query: ({ departureId, noteId }) => ({
        url: `/dashboard/${departureId}/note/${noteId}`,
        method: 'delete',
      }),
      invalidatesTags: (_, __, { departureId }) => ['Dashboard', { type: 'DashboardDetail', id: departureId }],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetDashboardDetailQuery,
  useAssignIncomingEmployeeMutation,
  useAddNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = managerDashboardApi;
